import { getIn } from "@utils/index"
import * as R from "ramda"

export const getBrands = (allProducts, brandOrder) =>
  R.pipe(
    R.map(({ brand }: any) => ({ label: brand.name })) as any,
    R.uniq,
    R.filter((brand) => !R.isNil(brand)),
    R.sort((a: any, b: any) => {
      const indexA = R.indexOf(a, brandOrder)
      return indexA === -1 ? 1 : indexA - R.indexOf(b, brandOrder)
    })
  )(allProducts)

export const getSubTypes = R.pipe(
  R.map((product: any) => ({
    label: getIn(product, "productSubType"),
  })),
  R.uniq,
  R.reject((subType: any) => R.isNil(subType) || R.isNil(subType?.label))
)

export const getColors = R.pipe(
  R.map(R.prop("variants")),
  R.flatten,
  R.map(({ color, hexString, swatchImage }) => ({
    hexString,
    label: color,
    swatchImage,
  })),
  R.uniq,
  R.reject((color) => R.isNil(color))
)

export const getSeries = R.pipe(
  R.map((product: any) => ({
    color: getIn(product, "series.color", undefined),
    icon: getIn(product, "series.icon.file.url", undefined),
    label: getIn(product, "series.name"),
  })),
  R.uniq,
  R.reject((series: any) => R.isNil(series) || R.isNil(series?.label))
)

export const getDiscountTiers = R.pipe(
  R.map(R.prop("variants")),
  R.flatten as any,
  R.countBy(R.prop("discount") as any)
)

export const sortSeries = (series, seriesOrder) =>
  R.sort((a: any, b: any) => {
    const indexA = R.findIndex((name) => name === a.label)(seriesOrder)
    const indexB = R.findIndex((name) => name === b.label)(seriesOrder)

    if (indexA === -1 && indexB !== -1) {
      return 1
    } else if (indexA !== -1 && indexB === -1) {
      return -1
    } else {
      return indexA - indexB
    }
  }, series)

export const getDeviceNameSlugMap = R.pipe(
  R.map(R.prop("devices")),
  R.flatten,
  R.uniq,
  R.reduce((acc, device) => {
    const name = getIn(device, "name")
    const slug = getIn(device, "slug")
    return { ...acc, [name]: slug }
  }, {})
)

export const getDevices = R.pipe(
  R.map(R.prop("devices")),
  R.flatten,
  R.map((device: any) => ({
    label: getIn(device, "name"),
  })),
  R.uniq
)

export const hasSubType = (product: any, subTypeFilters: any): any => {
  return R.find(({ label }) => label === getIn(product, "productSubType"))(
    subTypeFilters
  )
}

export const hasColor = (product: any, colorFilters: any): any => {
  const variants = getIn(product, "variants", [])
  return (
    variants &&
    R.find((variant: any): any => {
      const variantColor = getIn(variant, "color")
      return (
        variantColor &&
        R.find(({ label }) => label === variantColor)(colorFilters)
      )
    })(variants)
  )
}

export const hasBrand = (product: any, brandFilters: any): any => {
  return R.find(({ label }) => label === getIn(product, "brand.name"))(
    brandFilters
  )
}

export const hasCollections = (product: any, seriesFilters: any): any => {
  return R.find(({ label }) => label === getIn(product, "series.name"))(
    seriesFilters
  )
}
export const hasSeries = (product: any, seriesFilters: any): any => {
  return R.find(({ label }) => label === getIn(product, "series.name"))(
    seriesFilters
  )
}

export const hasDiscountTier = (
  product: any,
  discountTierFilters: any
): any => {
  const variants = getIn(product, "variants", [])
  return (
    variants &&
    R.find((variant: any): any => {
      const discount = getIn(variant, "discount")
      return discount > -1 && discount >= parseInt(discountTierFilters[0])
    })(variants)
  )
}

const isDiscounted = (variant: any): any => {
  const compareAtPrice = getIn(variant, "compareAtPrice")
  return compareAtPrice && parseInt(compareAtPrice) > parseInt(variant.price)
}

export const hasDiscounted = (product: any): any => {
  const variants: any = getIn(product, "variants", [])
  return variants && R.find(isDiscounted)(variants)
}

export const hasStock = (product: any): any => {
  const variants: any = getIn(product, "variants", [])
  return variants && R.find(({ isUnavailable }) => !isUnavailable)(variants)
}
